import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Plus,
    List,
    Wrench,
    LogOut,
    Menu,
    X,
    User,
    Repeat,
    Users,
    Settings2,
    Wallet,
    Bell,
    Search,
    Bot,
    Handshake,
    BarChart3,
    Scale,
    MapPin,
    MessageSquare,
    FileText,
    Heart,
} from "lucide-react";
import { useAuth } from "@/lib/AuthContext";
import { useEffect, useState as useStateNav } from "react";
import { api } from "@/lib/api";
import NewTaskAlert from "@/components/NewTaskAlert";
import NewPropostaAceiteAlert from "@/components/NewPropostaAceiteAlert";

const navAdmin = [
    { to: "/", label: "Painel", icon: LayoutDashboard },
    { to: "/conversas", label: "Conversas", icon: MessageSquare, badgeKey: "conversas" },
    { to: "/vendas", label: "Vendas", icon: List },
    { to: "/nova-venda", label: "Nova", icon: Plus },
    { to: "/instalacoes", label: "Instalar", icon: Wrench },
    { to: "/pos-venda", label: "Pós-venda", icon: Bot, badgeKey: "posvenda" },
    { to: "/recebimentos", label: "Receber", icon: Wallet },
    { to: "/alertas", label: "Alertas", icon: Bell },
    { to: "/fontes", label: "Fontes", icon: Users },
    { to: "/parceiros", label: "Parceiros", icon: Handshake },
    { to: "/leads-parceiros", label: "Influencers", icon: Users },
    { to: "/propostas", label: "Propostas", icon: FileText, badgeKey: "propostas_aceites" },
    { to: "/feedback", label: "Feedback", icon: Heart },
    { to: "/relatorio", label: "Relatório", icon: BarChart3 },
    { to: "/comparativo", label: "Comparativo", icon: Scale },
    { to: "/localizacao", label: "Mapa", icon: MapPin },
    { to: "/configuracoes", label: "Config.", icon: Settings2 },
];

const navColab = [
    { to: "/", label: "Vendas", icon: List },
    { to: "/nova-venda", label: "Nova Venda", icon: Plus },
    { to: "/instalacoes", label: "Instalações", icon: Wrench },
    { to: "/conversas", label: "Fecho do dia", icon: MessageSquare },
    { to: "/localizacao", label: "Mapa", icon: MapPin },
    { to: "/alertas", label: "Alertas", icon: Bell },
];

// Gestor: só vê a aba Parceiros (Influencers) — mais nada.
const navGestor = [
    { to: "/leads-parceiros", label: "Parceiros", icon: Handshake },
];

export default function Layout({ children }) {
    const { user, logout, switchRole } = useAuth();
    const location = useLocation();
    const navigate = useNavigate();
    const [open, setOpen] = useState(false);
    const [posVendaCount, setPosVendaCount] = useStateNav(0);
    const [conversasCount, setConversasCount] = useStateNav(0);
    const [propostasAceitesCount, setPropostasAceitesCount] = useStateNav(0);

    const nav = user?.role === "admin" ? navAdmin : user?.role === "gestor" ? navGestor : navColab;

    // Polling pós-venda (admin only)
    useEffect(() => {
        if (!user || user.role !== "admin") return;
        let cancelled = false;
        const fetchPos = async () => {
            try {
                const r = await api.get("/posvenda/messages?status=pending_approval&limit=1");
                if (!cancelled) setPosVendaCount(r.data?.pendentes || r.data?.total || 0);
            } catch (_) {
                // silencioso
            }
        };
        fetchPos();
        const id = setInterval(fetchPos, 30000);
        return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Polling conversas (unread count) — admin only (suporte não tem acesso a /conversas)
    useEffect(() => {
        if (!user || user.role !== "admin") return;
        let cancelled = false;
        const fetchConv = async () => {
            try {
                const r = await api.get("/conversas");
                if (!cancelled) setConversasCount(r.data?.unread || 0);
            } catch (_) {
                // silencioso
            }
        };
        fetchConv();
        const id = setInterval(fetchConv, 30000);
        return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    // Polling propostas aceites não-vistas (admin only)
    useEffect(() => {
        if (!user || user.role !== "admin") return;
        let cancelled = false;
        const fetchAceites = async () => {
            try {
                const r = await api.get("/propostas/aceites/count");
                if (!cancelled) setPropostasAceitesCount(r.data?.count || 0);
            } catch (_) {
                // silencioso
            }
        };
        fetchAceites();
        const id = setInterval(fetchAceites, 30000);
        return () => { cancelled = true; clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user]);

    const badgeFor = (item) => {
        if (item.badgeKey === "posvenda") return posVendaCount;
        if (item.badgeKey === "conversas") return conversasCount;
        if (item.badgeKey === "propostas_aceites") return propostasAceitesCount;
        return 0;
    };

    const handleLogout = () => {
        logout();
        navigate("/login");
    };

    const quickLinks = [
        { label: "Atlas", url: "https://atlas.meo.pt", color: "from-blue-500 to-blue-700" },
        { label: "Apolo", url: "https://apolo.meo.pt/login.php", color: "from-violet-500 to-violet-700" },
        { label: "MyDCV", url: "https://mydcv.meo.pt/MyDCV/", color: "from-emerald-500 to-emerald-700" },
    ];

    const handleSearch = (e) => {
        e.preventDefault();
        const q = e.target.elements.q?.value?.trim();
        if (!q) return;
        const url = `https://www.google.com/search?q=${encodeURIComponent(q)}`;
        window.open(url, "_blank", "noopener,noreferrer");
        e.target.reset();
    };

    return (
        <div className="min-h-screen flex bg-[#f9f9f9]">
            {/* Sidebar desktop */}
            <aside className="hidden lg:flex w-64 flex-col border-r border-zinc-200 bg-gradient-to-b from-white via-white to-blue-50/40">
                <div className="px-6 py-6 border-b border-zinc-200">
                    <div className="overline mb-1">Salvador • MEO</div>
                    <div className="font-display text-2xl font-black tracking-tight">
                        Controlo
                    </div>
                </div>
                <nav className="px-3 py-6 space-y-1">
                    {nav.map((item) => {
                        const Icon = item.icon;
                        const active = location.pathname === item.to;
                        const badge = badgeFor(item);
                        return (
                            <Link
                                key={item.to}
                                to={item.to}
                                data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
                                className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-all ${
                                    active
                                        ? "bg-[#002FE6] text-white shadow-sm shadow-blue-500/20"
                                        : "text-zinc-600 hover:bg-blue-50 hover:text-[#002FE6]"
                                }`}
                            >
                                <Icon size={18} />
                                <span className="flex-1">{item.label}</span>
                                {badge > 0 && (
                                    <span
                                        data-testid={`nav-badge-${item.label.toLowerCase()}`}
                                        className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center ${
                                            active ? "bg-white text-[#002FE6]" : "bg-red-500 text-white"
                                        }`}
                                    >
                                        {badge > 99 ? "99+" : badge}
                                    </span>
                                )}
                            </Link>
                        );
                    })}
                </nav>

                {/* Atalhos rápidos */}
                <div className="px-3 pb-4 pt-2 space-y-2">
                    <div className="overline px-1 mb-1">Atalhos</div>
                    <form onSubmit={handleSearch} className="relative" data-testid="quick-search-form">
                        <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                        <input
                            type="text"
                            name="q"
                            placeholder="Pesquisar no Google…"
                            data-testid="quick-search-input"
                            className="w-full pl-8 pr-2 py-2 text-sm border border-zinc-300 rounded-md bg-white focus:outline-none focus:border-[#002FE6] focus:ring-1 focus:ring-[#002FE6]/20"
                        />
                    </form>
                    <div className="grid grid-cols-3 gap-1.5">
                        {quickLinks.map((l) => (
                            <a
                                key={l.url}
                                href={l.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                data-testid={`quick-link-${l.label.toLowerCase()}`}
                                className={`flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white py-2 rounded-md bg-gradient-to-br ${l.color} hover:opacity-90 transition shadow-sm`}
                                title={l.url}
                            >
                                {l.label}
                            </a>
                        ))}
                    </div>
                </div>

                {/* Spacer empurra o footer (user/logout) para o fundo */}
                <div className="flex-1" />

                <div className="border-t border-zinc-200 p-4 space-y-3">
                    <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-[#002FE6] to-[#1E3FFF] text-white flex items-center justify-center text-sm font-bold shadow-md shadow-blue-500/30">
                            {user?.name?.[0] || "?"}
                        </div>
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">
                                {user?.name}
                            </div>
                            <div className="text-xs text-zinc-500 capitalize">
                                {user?.role}
                            </div>
                        </div>
                    </div>
                    {user?.role === "admin" && (
                        <button
                            data-testid="switch-role-btn"
                            onClick={() => switchRole("colaborador")}
                            className="w-full flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-900 py-2 border border-zinc-200 rounded-md hover:bg-zinc-50 transition"
                        >
                            <Repeat size={14} /> Ver como colaborador
                        </button>
                    )}
                    {user?.role === "colaborador" &&
                        user?.email === "salvador@meo.pt" && (
                            <button
                                data-testid="switch-role-back-btn"
                                onClick={() => switchRole("admin")}
                                className="w-full flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-900 py-2 border border-zinc-200 rounded-md hover:bg-zinc-50 transition"
                            >
                                <Repeat size={14} /> Voltar a Admin
                            </button>
                        )}
                    <button
                        data-testid="logout-btn"
                        onClick={handleLogout}
                        className="w-full flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wider text-zinc-500 hover:text-red-600 py-2 transition"
                    >
                        <LogOut size={14} /> Terminar sessão
                    </button>
                </div>
            </aside>

            {/* Mobile header */}
            <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-white border-b border-zinc-200">
                <div className="flex items-center justify-between px-4 h-14">
                    <div className="flex items-center gap-2">
                        <div className="font-display text-lg font-black">
                            Controlo
                        </div>
                        <div className="overline">Salvador</div>
                    </div>
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(!open)}
                        className="p-2"
                    >
                        {open ? <X size={20} /> : <Menu size={20} />}
                    </button>
                </div>
                {open && (
                    <div className="border-t border-zinc-200 bg-white">
                        {nav.map((item) => {
                            const Icon = item.icon;
                            const active = location.pathname === item.to;
                            const badge = badgeFor(item);
                            return (
                                <Link
                                    key={item.to}
                                    to={item.to}
                                    onClick={() => setOpen(false)}
                                    className={`flex items-center gap-3 px-5 py-3 text-sm font-medium border-b border-zinc-100 ${
                                        active
                                            ? "bg-[#002FE6] text-white"
                                            : "text-zinc-700"
                                    }`}
                                >
                                    <Icon size={18} />
                                    <span className="flex-1">{item.label}</span>
                                    {badge > 0 && (
                                        <span className={`min-w-[20px] h-5 px-1.5 rounded-full text-[10px] font-bold flex items-center justify-center ${active ? "bg-white text-[#002FE6]" : "bg-red-500 text-white"}`}>
                                            {badge > 99 ? "99+" : badge}
                                        </span>
                                    )}
                                </Link>
                            );
                        })}
                        {/* Atalhos no menu mobile */}
                        <div className="px-5 py-3 border-b border-zinc-100 bg-zinc-50/40 space-y-2">
                            <div className="overline">Atalhos</div>
                            <form onSubmit={(e) => { handleSearch(e); setOpen(false); }} className="relative">
                                <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
                                <input
                                    type="text"
                                    name="q"
                                    placeholder="Pesquisar no Google…"
                                    className="w-full pl-8 pr-2 py-2 text-sm border border-zinc-300 rounded-md bg-white"
                                />
                            </form>
                            <div className="grid grid-cols-3 gap-1.5">
                                {quickLinks.map((l) => (
                                    <a
                                        key={l.url}
                                        href={l.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        onClick={() => setOpen(false)}
                                        className={`flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white py-2 rounded-md bg-gradient-to-br ${l.color}`}
                                    >
                                        {l.label}
                                    </a>
                                ))}
                            </div>
                        </div>
                        <div className="px-5 py-3 flex items-center justify-between">
                            <div className="flex items-center gap-2 text-sm">
                                <User size={16} />
                                <span className="font-semibold">
                                    {user?.name}
                                </span>
                                <span className="text-zinc-500 capitalize">
                                    · {user?.role}
                                </span>
                            </div>
                            <button
                                onClick={handleLogout}
                                className="text-xs uppercase tracking-wider text-red-600 font-semibold"
                            >
                                Sair
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Bottom tab nav mobile */}
            <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-zinc-200">
                <div
                    className="grid"
                    style={{
                        gridTemplateColumns: `repeat(${nav.length}, minmax(0, 1fr))`,
                    }}
                >
                    {nav.map((item) => {
                        const Icon = item.icon;
                        const active = location.pathname === item.to;
                        const badge = badgeFor(item);
                        return (
                            <Link
                                key={item.to}
                                to={item.to}
                                className={`relative flex flex-col items-center justify-center py-2 text-[9px] font-semibold uppercase tracking-wider ${
                                    active ? "text-[#002FE6]" : "text-zinc-500"
                                }`}
                            >
                                <Icon size={16} />
                                {badge > 0 && (
                                    <span className="absolute top-1 right-1/2 translate-x-3 min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold bg-red-500 text-white flex items-center justify-center leading-none">
                                        {badge > 9 ? "9+" : badge}
                                    </span>
                                )}
                                <span className="mt-0.5 truncate max-w-full px-1">
                                    {item.label}
                                </span>
                            </Link>
                        );
                    })}
                </div>
            </div>

            <main className="flex-1 lg:ml-0 pt-14 lg:pt-0 pb-20 lg:pb-0">
                {children}
            </main>

            {/* Popup global: alerta sempre que aparece nova tarefa Kommo */}
            <NewTaskAlert />
            <NewPropostaAceiteAlert />
        </div>
    );
}
